import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { OrgViewBanner } from "@/components/OrgViewBanner";
import { UpgradeBar } from "@/components/UpgradeBar";
import { SupportWidget } from "@/components/SupportWidget";
import { AssistantFab } from "@/components/AssistantFab";
import { PageHelpProvider } from "@/context/PageHelpContext";
import { captureUtmParams } from "@/utils/utm";
import LoginPage from "@/pages/LoginPage";
import SignUpPage from "@/pages/SignUpPage";
import PostCheckoutPage from "@/pages/PostCheckoutPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AuthCallback from "@/pages/AuthCallback";
import DashboardPage from "@/pages/DashboardPage";
import MinutesPage from "@/pages/MinutesPage";
import MinutesDetailPage from "@/pages/MinutesDetailPage";
import MinutesTemplateFormPage from "@/pages/MinutesTemplateFormPage";
import CreateMinutesPage from "@/pages/CreateMinutesPage";
import DistributionsPage from "@/pages/DistributionsPage";
import ExpensesPage from "@/pages/ExpensesPage";
import GovernancePage from "@/pages/GovernancePage";
import SettingsPage from "@/pages/SettingsPage";
import OnboardingPage from "@/pages/OnboardingPage";
import TrustCalendarPage from "@/pages/TrustCalendarPage";
import EntityDetailPage from "@/pages/EntityDetailPage";
import StructuresPage from "@/pages/StructuresPage";
import CompensationPage from "@/pages/CompensationPage";
import BillingPage from "@/pages/BillingPage";
import ScheduleAPage from "@/pages/ScheduleAPage";
import BenevolencePage from "@/pages/BenevolencePage";
import BenevolencePolicyPage from "@/pages/BenevolencePolicyPage";
import PricingPage from "@/pages/PricingPage";
import AuthorityPage from "@/pages/AuthorityPage";
import AuditTrailPage from "@/pages/AuditTrailPage";
import BeneficiariesPage from "@/pages/BeneficiariesPage";
import AffiliatePage from "@/pages/AffiliatePage";
import AdminPage from "@/pages/AdminPage";
import LeadsAccessPage from "@/pages/LeadsAccessPage";
import StatsPage from "@/pages/StatsPage";
import StateCompliancePage from "@/pages/StateCompliancePage";
import MeetingAgenda from "@/pages/MeetingAgenda";
import MinutesEditor from "@/pages/MinutesEditor";
import ApprovalFlow from "@/pages/ApprovalFlow";
import MeetingHistory from "@/pages/MeetingHistory";
import ClientsListPage from "@/pages/ClientsListPage";
import TrustAccessPage from "@/pages/TrustAccessPage";
import OrgConsolePage from "@/pages/OrgConsolePage";
import ClientDashboard from "@/pages/ClientDashboard";
import HealthDashboard from "@/pages/HealthDashboard";
import DeadlineDashboard from "@/pages/DeadlineDashboard";
import InvestmentsPage from "@/pages/InvestmentsPage";
import CommunicationsPage from "@/pages/CommunicationsPage";
import VaultPage from "@/pages/VaultPage";
import MessagingPage from "@/pages/MessagingPage";
import RiskDashboardPage from "@/pages/RiskDashboardPage";
import PrintableBinderPage from "@/pages/PrintableBinderPage";
import SuccessorPacketPage from "@/pages/SuccessorPacketPage";
import SuccessorAccessPage from "@/pages/SuccessorAccessPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import TrustAssistantPage from "@/pages/TrustAssistantPage";
import BeneficiaryReportPage from "@/pages/BeneficiaryReportPage";
import ExportDashboard from "@/pages/ExportDashboard";
import WingPointWelcomePage from "@/pages/WingPointWelcomePage";
import WingPointRedirect from "@/pages/WingPointRedirect";
import ExternalRedirect from "@/pages/ExternalRedirect";
import ConnectWingPoint from "@/pages/ConnectWingPoint";
import NotFoundPage from "@/pages/NotFoundPage";
import KnowledgeBasePage from "@/pages/KnowledgeBasePage";
import KnowledgeArticleDetail from "@/pages/KnowledgeArticleDetail";
import KnowledgeAdmin from "@/pages/KnowledgeAdmin";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import { UpgradeModalProvider } from "@/context/UpgradeModalContext";
import { SubscriptionGate, FullSubscriptionGate } from "@/components/SubscriptionGate";
import { installGlobalErrorHandlers } from "@/utils/errors";
import CoursePage from "@/pages/CoursePage";
import TrustAdminKitsPage from "@/pages/TrustAdminKitsPage";
import TransactionLedgerPage from "@/pages/TransactionLedgerPage";
import BenevolenceLogPage from "@/pages/BenevolenceLogPage";
import PerformanceDashboard from "@/pages/PerformanceDashboard";
import InvestmentsTabbed from "@/pages/InvestmentsTabbed";
import BenevolenceTabbed from "@/pages/BenevolenceTabbed";
import HealthComplianceTabbed from "@/pages/HealthComplianceTabbed";
import DocumentsTabbed from "@/pages/DocumentsTabbed";

// Install global uncaught error handlers once at app load
// Reports to /api/report-error -> Discord alert (see utils/errors.js)
installGlobalErrorHandlers();

// GA4 page view tracking for SPA navigation
const useGA4PageTracking = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Ensure gtag is available and send page view on route change
    if (typeof window.gtag === 'function') {
      window.gtag('config', 'G-MT6FBPRE60', {
        page_path: location.pathname + location.search,
        page_title: document.title
      });
    }
  }, [location]);
};

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { user, loading, trusts, trustsLoading } = useAuth();
  const location = useLocation();
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  // Timeout to prevent infinite loading - after 10 seconds, proceed anyway
  useEffect(() => {
    const timer = setTimeout(() => {
      if (loading || trustsLoading) {
        console.warn('[ProtectedRoute] Loading timeout reached, proceeding anyway');
        setLoadingTimeout(true);
      }
    }, 10000);
    return () => clearTimeout(timer);
  }, [loading, trustsLoading]);

  // If user was passed via navigation state (e.g., from AuthCallback), use it
  const hasUserFromState = location.state?.user;

  // Check if this is an OAuth callback route
  const isCallback = location.pathname === '/auth/callback' || location.pathname === '/auth/google/callback';

  // Check if user is admin - admins skip onboarding
  const isAdmin = user?.is_admin || user?.email?.toLowerCase() === 'contact@trustoffice.app';

  // Check for stored token
  const hasToken = localStorage.getItem('auth_token') !== null;

  const isOnboarding = location.pathname.includes('/onboarding');
  const skipOnboarding = localStorage.getItem('skip_onboarding') === 'true';

  // IMMEDIATE redirect: no token, no user, not loading — go to login
  if (shouldRedirectToLogin({ hasToken, user, loading })) {
    return <Navigate to="/" replace />;
  }

  // Show loading spinner while auth is being verified (but not for trusts on onboarding)
  if (shouldShowLoadingSpinner({ loading, isOnboarding, trustsLoading, loadingTimeout })) {
    return (
      <div className="min-h-screen bg-subtle-bg flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-navy border-t-transparent animate-spin mx-auto mb-4"></div>
          <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Loading</p>
        </div>
      </div>
    );
  }

  // After loading timeout, if we still have a token, don't redirect - let user see the page
  // Only redirect to login if there's genuinely no token
  if (shouldRedirectToLoginPostTimeout({ hasToken, user, hasUserFromState, isCallback })) {
    return <Navigate to="/" replace />;
  }

  // Redirect new users (no trusts) to onboarding, except:
  // - Already on onboarding
  // - User is admin
  // - Still loading trusts
  // - User has explicit "skip onboarding" in localStorage (set when clicking read-only mode)
  // - User has a token but user data hasn't loaded yet (loading timeout scenario)
  if (shouldRedirectToOnboarding({ user, trustsLoading, trusts, isOnboarding, isAdmin, skipOnboarding, pathname: location.pathname })) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// Guard: immediate redirect when there's no token, no user, and auth isn't loading.
const shouldRedirectToLogin = ({ hasToken, user, loading }) =>
  !hasToken && !user && !loading;

// Guard: show the loading spinner while auth/trusts are being verified
// (unless the loading timeout has fired, or we're on onboarding which
// doesn't need trusts loaded).
const shouldShowLoadingSpinner = ({ loading, isOnboarding, trustsLoading, loadingTimeout }) =>
  (loading || (!isOnboarding && trustsLoading)) && !loadingTimeout;

// Guard: post-timeout redirect to login only if there's genuinely no token,
// no user, no user-from-navigation-state, and not a callback route.
const shouldRedirectToLoginPostTimeout = ({ hasToken, user, hasUserFromState, isCallback }) =>
  !hasToken && !user && !hasUserFromState && !isCallback;

// Guard: redirect users with no trusts to onboarding, unless they're already
// there, are admins, are still loading trusts, or have opted out via localStorage.
// Org surfaces (/org-console, /trust-access) are exempt: org members can be
// active with zero personal trusts before any grant reaches them — trapping
// them in the upload wizard locks them out of their console entirely.
const ONBOARDING_EXEMPT_ORG_PREFIXES = ['/org-console', '/trust-access'];
const isOnboardingExemptOrgRoute = (pathname) =>
  ONBOARDING_EXEMPT_ORG_PREFIXES.some(p => pathname === p || pathname.startsWith(`${p}/`));
const shouldRedirectToOnboarding = ({ user, trustsLoading, trusts, isOnboarding, isAdmin, skipOnboarding, pathname }) =>
  user && !trustsLoading && trusts && trusts.length === 0 && !isOnboarding && !isAdmin && !skipOnboarding && !isOnboardingExemptOrgRoute(pathname);

// Protected Route with Subscription Gate (for routes that require active subscription)
const SubscriptionProtectedRoute = ({ children }) => {
  return (
    <ProtectedRoute>
      <SubscriptionGate>
        {children}
      </SubscriptionGate>
    </ProtectedRoute>
  );
};

// Hard paywall for Trust Assistant: users retain their records elsewhere,
// but non-paying/read-only accounts cannot use AI chat or its actions.
const FullSubscriptionProtectedRoute = ({ children }) => {
  return (
    <ProtectedRoute>
      <FullSubscriptionGate>
        {children}
      </FullSubscriptionGate>
    </ProtectedRoute>
  );
};

// App Router with session_id detection and GA4 tracking
const AppRouter = () => {
  const location = useLocation();
  
  // Track page views in GA4 on route changes
  useGA4PageTracking();

  // Capture UTM/attribution params from the URL into sessionStorage so they
  // survive navigation to signup/checkout (ad campaign attribution).
  useEffect(() => {
    captureUtmParams();
  }, [location]);

  // Scroll to top on route change — SPA navigation otherwise keeps the
  // previous page's scroll offset, so clicking a nav item (e.g. Trust Roles)
  // lands the user mid-page instead of at the top.
  // Keyed on pathname only: search-param-only changes (tab switches) must not
  // yank the scroll. Skipped when the target URL carries a #hash so pages
  // with hash-anchor deep links (SettingsPage) keep control of positioning.
  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname]);

  // CRITICAL: Check URL fragment synchronously for session_id (OAuth callback)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LoginPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignUpPage />} />
      <Route path="/register" element={<SignUpPage />} />
      <Route path="/post-checkout" element={<PostCheckoutPage />} />
      <Route path="/wingpoint" element={<WingPointRedirect />} />
      {/* 2026-10-05: legacy booking/course links in lead emails 404'd here — heal to marketing site */}
      <Route path="/book" element={<ExternalRedirect to="https://trustoffice.app/book-a-call/" />} />
      <Route path="/book-a-call" element={<ExternalRedirect to="https://trustoffice.app/book-a-call/" />} />
      <Route path="/book-a-call/" element={<ExternalRedirect to="https://trustoffice.app/book-a-call/" />} />
      <Route path="/meeting" element={<ExternalRedirect to="https://trustoffice.app/book-a-call/" />} />
      <Route path="/trustee-101" element={<ExternalRedirect to="https://trustoffice.app/trustee-101/" />} />
      {/* Connect to WingPoint — account linking flow (public, checks auth internally) */}
      <Route path="/connect/wingpoint" element={<ConnectWingPoint />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/affiliate" element={<AffiliatePage />} />
      <Route path="/help" element={<Navigate to="/dashboard" replace />} />
      <Route path="/about" element={<Navigate to="/" replace />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Public successor trustee access link — intentionally outside all auth gates */}
      <Route path="/successor-access/:token" element={<SuccessorAccessPage />} />
      <Route path="/invite/:token" element={<AcceptInvitePage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/google/callback" element={<AuthCallback />} />
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <OnboardingPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <SubscriptionProtectedRoute>
          <DashboardPage />
        </SubscriptionProtectedRoute>
      } />
      <Route path="/calendar" element={
        <SubscriptionProtectedRoute>
          <TrustCalendarPage />
        </SubscriptionProtectedRoute>
      } />
      <Route path="/minutes" element={
        <SubscriptionProtectedRoute>
          <MinutesPage />
        </SubscriptionProtectedRoute>
      } />
      <Route path="/minutes/:minutesId" element={
        <SubscriptionProtectedRoute>
          <MinutesDetailPage />
        </SubscriptionProtectedRoute>
      } />
      <Route path="/minutes/new" element={<Navigate to="/minutes/create" replace />} />
      {/* Redirect old templates list to new unified wizard */}
      <Route path="/minutes/templates" element={<Navigate to="/minutes/create" replace />} />
      {/* Template form — used by CreateMinutesPage template selection */}
      <Route path="/minutes/template/:templateType" element={
        <SubscriptionProtectedRoute>
          <MinutesTemplateFormPage />
        </SubscriptionProtectedRoute>
      } />
      <Route path="/guided-minutes" element={<Navigate to="/minutes/create" replace />} />
      <Route path="/retroactive-minutes" element={<Navigate to="/minutes/create?retroactive=1" replace />} />
      <Route path="/minutes/create" element={
        <SubscriptionProtectedRoute>
          <CreateMinutesPage />
        </SubscriptionProtectedRoute>
      } />
      <Route path="/schedule-a" element={
        <SubscriptionProtectedRoute>
          <ScheduleAPage />
        </SubscriptionProtectedRoute>
      } />
      <Route path="/trust/units" element={
        <Navigate to="/beneficiaries" replace />
      } />
      <Route path="/trust/beneficiaries" element={
        <Navigate to="/beneficiaries" replace />
      } />
      <Route path="/beneficiaries" element={
        <SubscriptionProtectedRoute>
          <BeneficiariesPage />
        </SubscriptionProtectedRoute>
      } />
      <Route path="/benevolence" element={
        <SubscriptionProtectedRoute>
          <BenevolenceTabbed />
        </SubscriptionProtectedRoute>
      } />
      {/* Redirect old /benevolence/policy to /benevolence?tab=policy */}
      <Route path="/benevolence/policy" element={<Navigate to="/benevolence?tab=policy" replace />} />
      <Route path="/benevolence-log" element={ <Navigate to="/benevolence" replace /> } />
      <Route path="/distributions" element={
        <SubscriptionProtectedRoute>
          <DistributionsPage />
        </SubscriptionProtectedRoute>
      } />
      <Route path="/transactions" element={
        <SubscriptionProtectedRoute>
          <TransactionLedgerPage />
        </SubscriptionProtectedRoute>
      } />
      <Route path="/compensation" element={
        <SubscriptionProtectedRoute>
          <CompensationPage />
        </SubscriptionProtectedRoute>
      } />
      <Route path="/expenses" element={
        <SubscriptionProtectedRoute>
          <ExpensesPage />
        </SubscriptionProtectedRoute>
      } />
      <Route path="/structures" element={
        <SubscriptionProtectedRoute>
          <StructuresPage />
        </SubscriptionProtectedRoute>
      } />
      <Route path="/authority" element={
        <SubscriptionProtectedRoute>
          <AuthorityPage />
        </SubscriptionProtectedRoute>
      } />
      {/* Removed duplicate retroactive-minutes route — redirect added above */}
      <Route path="/audit-trail" element={
        <SubscriptionProtectedRoute>
          <AuditTrailPage />
        </SubscriptionProtectedRoute>
      } />
      {/* Redirect old routes to new unified Structures page */}
      <Route path="/entities" element={<Navigate to="/structures?tab=entities" replace />} />
      <Route path="/structure" element={<Navigate to="/structures?tab=hierarchy" replace />} />
      <Route path="/entities/:entityId" element={
        <SubscriptionProtectedRoute>
          <EntityDetailPage />
        </SubscriptionProtectedRoute>
      } />
      <Route path="/governance" element={
        <SubscriptionProtectedRoute>
          <HealthComplianceTabbed />
        </SubscriptionProtectedRoute>
      } />
      <Route path="/governance/agendas/:agendaId" element={
        <SubscriptionProtectedRoute>
          <MeetingAgenda />
        </SubscriptionProtectedRoute>
      } />
      <Route path="/governance/minutes/:minutesId" element={
        <SubscriptionProtectedRoute>
          <MinutesEditor />
        </SubscriptionProtectedRoute>
      } />
      <Route path="/governance/approvals/:trustId" element={
        <SubscriptionProtectedRoute>
          <ApprovalFlow />
        </SubscriptionProtectedRoute>
      } />
      <Route path="/governance/history/:trustId" element={
        <SubscriptionProtectedRoute>
          <MeetingHistory />
        </SubscriptionProtectedRoute>
      } />
      <Route path="/clients" element={
        <SubscriptionProtectedRoute>
          <ClientsListPage />
        </SubscriptionProtectedRoute>
      } />
      <Route path="/trust-access" element={
        <ProtectedRoute>
          <TrustAccessPage />
        </ProtectedRoute>
      } />
      <Route path="/org-console" element={
        <ProtectedRoute>
          <OrgConsolePage />
        </ProtectedRoute>
      } />
      <Route path="/clients/:clientId" element={
        <SubscriptionProtectedRoute>
          <ClientDashboard />
        </SubscriptionProtectedRoute>
      } />
      {/* /health redirects to /governance (Trust Health) — HealthDashboard is superseded */}
      <Route path="/health" element={<Navigate to="/governance" replace />} />
      {/* /deadlines redirects to /calendar — DeadlineDashboard is superseded */}
      <Route path="/deadlines" element={<Navigate to="/calendar" replace />} />
      <Route path="/beneficiary-reports" element={
        <SubscriptionProtectedRoute>
          <BeneficiaryReportPage />
        </SubscriptionProtectedRoute>
      } />
      <Route path="/exports" element={
        <SubscriptionProtectedRoute>
          <ExportDashboard />
        </SubscriptionProtectedRoute>
      } />
      <Route path="/tax-calendar" element={
        <Navigate to="/calendar" replace />
      } />
      <Route path="/trust-health" element={
        <Navigate to="/governance" replace />
      } />
      {/* Redirect old /state-compliance to /governance?tab=state */}
      <Route path="/state-compliance" element={<Navigate to="/governance?tab=state" replace />} />
      <Route path="/investments" element={
        <SubscriptionProtectedRoute>
          <InvestmentsTabbed />
        </SubscriptionProtectedRoute>
      } />
      {/* Redirect old /performance to /investments?tab=performance */}
      <Route path="/performance" element={<Navigate to="/investments?tab=performance" replace />} />
      <Route path="/communications" element={
        <SubscriptionProtectedRoute>
          <CommunicationsPage />
        </SubscriptionProtectedRoute>
      } />
      <Route path="/messaging" element={
        <SubscriptionProtectedRoute>
          <MessagingPage />
        </SubscriptionProtectedRoute>
      } />
      <Route path="/vault" element={
        <SubscriptionProtectedRoute>
          <DocumentsTabbed />
        </SubscriptionProtectedRoute>
      } />
      {/* Redirect old /risk to /governance?tab=risk */}
      <Route path="/risk" element={<Navigate to="/governance?tab=risk" replace />} />
      {/* Trust Assistant is a write/AI feature: free and expired accounts
          may retain their records, but must not open or use chat. */}
      <Route path="/trust-assistant" element={
        <FullSubscriptionProtectedRoute>
          <TrustAssistantPage />
        </FullSubscriptionProtectedRoute>
      } />
<Route path="/course" element={<ProtectedRoute><CoursePage /></ProtectedRoute>} />
      <Route path="/course/lesson/:lessonNumber" element={<ProtectedRoute><CoursePage /></ProtectedRoute>} />
      {/* Redirect old /binder to /vault?tab=binder */}
      <Route path="/binder" element={<Navigate to="/vault?tab=binder" replace />} />
      <Route path="/successor-packet" element={
        <SubscriptionProtectedRoute>
          <SuccessorPacketPage />
        </SubscriptionProtectedRoute>
      } />
      {/* Redirect old /admin-kits to /vault?tab=templates */}
      <Route path="/admin-kits" element={<Navigate to="/vault?tab=templates" replace />} />
      {/* Knowledge Base / Resource Hub */}
      <Route path="/knowledge" element={
        <SubscriptionProtectedRoute>
          <KnowledgeBasePage />
        </SubscriptionProtectedRoute>
      } />
      <Route path="/knowledge/:id" element={
        <SubscriptionProtectedRoute>
          <KnowledgeArticleDetail />
        </SubscriptionProtectedRoute>
      } />
      <Route path="/knowledge/admin" element={
        <ProtectedRoute>
          <KnowledgeAdmin />
        </ProtectedRoute>
      } />
      {/* Settings and Billing are accessible without active subscription */}
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/trust-roles" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/settings/billing" element={
        <ProtectedRoute>
          <BillingPage />
        </ProtectedRoute>
      } />
      {/* Admin panel - only accessible to admins */}
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminPage />
        </ProtectedRoute>
      } />
      {/* Leads area - accessible to leads users and admins */}
      <Route path="/leads" element={
        <ProtectedRoute>
          <LeadsAccessPage />
        </ProtectedRoute>
      } />
      {/* Stats dashboard - accessible to stats users and admins */}
      <Route path="/stats" element={
        <ProtectedRoute>
          <StatsPage />
        </ProtectedRoute>
      } />

      {/* Catch-all 404 route - must be last */}
      <Route path="/performance" element={
        <SubscriptionProtectedRoute>
          <PerformanceDashboard />
        </SubscriptionProtectedRoute>
      } />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

// Conditionally renders the SupportWidget only when the user is authenticated.
// Lives inside <BrowserRouter> so it can use useLocation, and inside
// <AuthProvider> so it can use useAuth. Public routes (login/register/etc.)
// never get the widget because `user` is null there.
const SupportWidgetMount = () => {
  const { user } = useAuth();
  if (!user) return null;
  return <SupportWidget />;
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <PageHelpProvider>
            <UpgradeModalProvider>
              <ErrorBoundary>
                <UpgradeBar />
                <ImpersonationBanner />
                <OrgViewBanner />
                <AppRouter />
                <SupportWidgetMount />
                <AssistantFab />
                <Toaster position="top-right" />
              </ErrorBoundary>
            </UpgradeModalProvider>
          </PageHelpProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
